/**
 * ConfirmDialog — promise-based replacement for `window.confirm`.
 *
 * Three variants, all built on `createImperativeDialog` + `MessageDialog`:
 *   - `showConfirmDialog(message)` → 2-button Cancel/Discard, resolves boolean
 *   - `showSaveOrDiscardDialog()`  → 3-button Cancel/Discard/Save, resolves
 *     "save" | "discard" | "cancel"
 *   - `showApplyGeneratorDialog(isGroup)` → 2-button Cancel/Apply, resolves boolean
 */

import { AlertTriangle } from "lucide-react";
import { createImperativeDialog } from "./imperativeDialog.tsx";
import { MessageDialog } from "./ModalShell.tsx";
import { COLOR_CAUTION } from "./tokens.ts";

// ---------------------------------------------------------------------------
// Two-button confirm (Cancel / Discard)
// ---------------------------------------------------------------------------

const confirmDialog = createImperativeDialog<{ message: string }, boolean>({
  render: ({ message }, close) => (
    <MessageDialog
      icon={AlertTriangle}
      iconColor={COLOR_CAUTION}
      title="Unsaved Changes"
      message={message}
      onClose={() => close(false)}
      width={380}
      actions={[
        { label: "Cancel", onClick: () => close(false) },
        {
          label: "Discard",
          kind: "primary",
          onClick: () => close(true),
          autoFocus: true,
        },
      ]}
    />
  ),
});

/**
 * Show a confirmation dialog. Resolves `true` on confirm, `false` on cancel.
 * Drop-in replacement for `window.confirm`.
 */
export const showConfirmDialog = (message: string): Promise<boolean> =>
  confirmDialog.show({ message });

export const ConfirmDialog = confirmDialog.Host;

// ---------------------------------------------------------------------------
// Three-button Save / Discard / Cancel
// ---------------------------------------------------------------------------

export type SaveOrDiscardResult = "save" | "discard" | "cancel";

const saveOrDiscardDialog = createImperativeDialog<
  Record<string, never>,
  SaveOrDiscardResult
>({
  render: (_args, close) => (
    <MessageDialog
      icon={AlertTriangle}
      iconColor={COLOR_CAUTION}
      title="Unsaved Changes"
      message="You have unsaved changes. What would you like to do?"
      onClose={() => close("cancel")}
      width={380}
      actions={[
        { label: "Cancel", onClick: () => close("cancel") },
        {
          label: "Discard",
          color: COLOR_CAUTION,
          onClick: () => close("discard"),
        },
        {
          label: "Save & Leave",
          kind: "primary",
          onClick: () => close("save"),
        },
      ]}
    />
  ),
});

/** Show a three-option dialog: Save, Discard, or Cancel. */
export const showSaveOrDiscardDialog = (): Promise<SaveOrDiscardResult> =>
  saveOrDiscardDialog.show({});

export const SaveOrDiscardDialog = saveOrDiscardDialog.Host;

// ---------------------------------------------------------------------------
// Apply Generator confirmation dialog
// ---------------------------------------------------------------------------

const applyGeneratorDialog = createImperativeDialog<
  { isGroup: boolean },
  boolean
>({
  render: ({ isGroup }, close) => (
    <MessageDialog
      icon={AlertTriangle}
      iconColor={COLOR_CAUTION}
      title="Apply Generator"
      message={
        isGroup
          ? "This object belongs to a generator group. Applying the generator will make all objects in the group permanent and remove the generator controls."
          : "To enter edit mode, the generator must be applied first. This will make the geometry permanent and remove the generator controls."
      }
      onClose={() => close(false)}
      width={400}
      actions={[
        { label: "Cancel", onClick: () => close(false) },
        {
          label: "Apply Generator",
          kind: "primary",
          onClick: () => close(true),
          autoFocus: true,
        },
      ]}
    />
  ),
});

/**
 * Show a confirmation dialog before applying a generator.
 * Resolves `true` on confirm (apply), `false` on cancel.
 */
export const showApplyGeneratorDialog = (isGroup: boolean): Promise<boolean> =>
  applyGeneratorDialog.show({ isGroup });

export const ApplyGeneratorDialog = applyGeneratorDialog.Host;
